// plan-16 S03: shell command dispatcher
// Handles ls, cd, cat, pwd, play, .., ~, AUTO_CD
// Runs in parallel with the Oracle command registry — shell commands take priority.

import * as fs from './fs'
import { resolve as resolvePath, basename } from './path'
import { tokenize } from './tokenize'
import type { CommandCtx } from '../../oracle/commands'
import { createStreamTarget } from '../../oracle/streaming'
import { load, set } from '../../core/save'
import { emit } from '../../core/bus'

const SHELL_CMDS = new Set(['ls', 'cd', 'cat', 'pwd', 'play', 'tree'])

let cwd = '/'
let prevCwd: string | null = null

export function getCwd(): string {
  return cwd
}

export function setCwd(path: string): void {
  cwd = path
}

export function isShellCommand(input: string): boolean {
  const first = input.trim().split(/\s+/)[0]?.toLowerCase() ?? ''
  return SHELL_CMDS.has(first)
}

export function isShellBare(input: string): boolean {
  const trimmed = input.trim()
  // .. and ~ typed alone
  if (trimmed === '..' || trimmed === '~') return true
  return false
}

export async function tryAutoCd(input: string): Promise<boolean> {
  const trimmed = input.trim()
  if (!trimmed) return false
  // Don't intercept known Oracle or shell commands
  const firstToken = tokenize(trimmed)[0]?.value ?? ''
  const lower = firstToken.toLowerCase()
  if (SHELL_CMDS.has(lower)) return false

  // Check if it resolves to a directory
  const target = resolvePath(firstToken, cwd)
  const stat = await fs.stat(target)
  if ('error' in stat) {
    // Try as display name under VPK_SERIES
    if (cwd === '/' || cwd === '/VPK_SERIES') {
      const dirCheck = await fs.stat('/VPK_SERIES/' + firstToken)
      if (!('error' in dirCheck) && fs.isDir(dirCheck)) {
        await handleCd('/VPK_SERIES/' + firstToken)
        return true
      }
    }
    return false
  }
  if (fs.isDir(stat)) {
    await handleCd(target)
    return true
  }
  return false
}

// Shell dispatch — returns true if handled
export async function dispatchShell(
  input: string,
  ctx: CommandCtx,
): Promise<boolean> {
  const trimmed = input.trim()
  if (!trimmed) return false

  // Handle bare .. and ~
  if (trimmed === '..') {
    await handleCd('..')
    return true
  }
  if (trimmed === '~') {
    await handleCd('~')
    return true
  }

  // Tokenize to get the command and args
  const tokens = tokenize(trimmed)
  if (tokens.length === 0) return false

  const cmd = tokens[0].value.toLowerCase()
  const rawArgs = tokens.slice(1)
  // Join remaining tokens as a single path (handles quoted paths)
  const argStr = rawArgs.map(t => t.value).join(' ')

  switch (cmd) {
    case 'ls':
      await handleLs(argStr, ctx)
      return true
    case 'cd':
      await handleCdWithCtx(argStr || undefined, ctx)
      return true
    case 'cat':
      await handleCat(argStr, ctx)
      return true
    case 'pwd':
      await handlePwd(ctx)
      return true
    case 'play':
      await handlePlay(argStr, ctx)
      return true
    case 'tree':
      // alias for ls (depth 1 only)
      await handleLs(argStr, ctx)
      return true
    default:
      return false
  }
}

async function handleLs(arg: string, ctx: CommandCtx): Promise<void> {
  // ls -la is accepted (alias for plain ls)
  const cleanArg = arg.replace(/^-la$|^--la$|-l$|-a$/g, '').trim()
  const target = cleanArg ? resolvePath(cleanArg, cwd) : cwd

  const result = await fs.list(target)
  if ('error' in result) {
    await streamDim(ctx, `ls: ${cleanArg || cwd}: no such directory.`)
    return
  }

  if (result.length === 0) {
    await streamDim(ctx, '// empty directory.')
    return
  }

  // Render directory listing
  const parent = ctx.history
  const container = document.createElement('div')
  container.className = 'vt-fs-listing'

  for (const node of result) {
    const row = document.createElement('div')
    row.className = 'vt-fs-row'

    const kind = document.createElement('span')
    kind.className = 'vt-fs-kind'
    kind.textContent = node.kind === 'dir' ? 'dir' : fs.isVideo(node) ? 'mp4' : 'txt'
    row.appendChild(kind)

    const size = document.createElement('span')
    size.className = 'vt-fs-size'
    size.textContent = node.size
    row.appendChild(size)

    const name = document.createElement('span')
    name.className = 'vt-fs-name'
    if (fs.isVideo(node)) {
      // Clickable button for video files
      const btn = document.createElement('button')
      btn.className = 'vt-fs-play'
      btn.textContent = node.name
      btn.setAttribute('aria-label', `play ${node.name}`)
      const videoNode = node as fs.FsFileNode
      btn.addEventListener('click', () => {
        emit('filesystem:play', { path: videoNode.urlPath, displayName: videoNode.displayName })
      })
      name.appendChild(btn)
    } else if (fs.isDir(node)) {
      name.textContent = node.name + '/'
    } else {
      name.textContent = node.name
    }
    row.appendChild(name)

    container.appendChild(row)
  }

  parent.appendChild(container)
  scheduleAnchor()
}

async function handleCd(arg?: string): Promise<void> {
  if (!arg || arg === '~') {
    // cd home (root)
    prevCwd = cwd
    cwd = '/'
    persistCwd()
    return
  }
  if (arg === '-') {
    // cd to previous
    if (prevCwd) {
      const tmp = cwd
      cwd = prevCwd
      prevCwd = tmp
    }
    persistCwd()
    return
  }

  const target = resolvePath(arg, cwd)

  // Resolve display-name paths to virtual FS paths
  const resolved = await resolveDisplayName(target)
  const stat = await fs.stat(resolved)
  if ('error' in stat) {
    // Stream error silently — cd failures don't produce output in our spec
    // Actually the plan says: > cd: <path>: no such directory.
    // But we can't stream here without ctx. Let's handle errors differently.
    // The caller (dispatchShell) passes ctx only for output-producing commands.
    // For cd, we'll just silently fail (the prompt path doesn't change).
    return
  }

  if (!fs.isDir(stat)) {
    return
  }

  prevCwd = cwd
  cwd = resolved
  persistCwd()
}

async function handleCdWithCtx(arg: string | undefined, ctx: CommandCtx): Promise<void> {
  if (!arg || arg === '~') {
    prevCwd = cwd
    cwd = '/'
    persistCwd()
    return
  }
  if (arg === '-') {
    if (prevCwd) {
      const tmp = cwd
      cwd = prevCwd
      prevCwd = tmp
    }
    persistCwd()
    return
  }

  const target = resolvePath(arg, cwd)
  const resolved = await resolveDisplayName(target)
  const stat = await fs.stat(resolved)
  if ('error' in stat) {
    await streamDim(ctx, `cd: ${arg}: no such directory.`)
    return
  }
  if (!fs.isDir(stat)) {
    await streamDim(ctx, `cd: ${arg}: not a directory.`)
    return
  }

  prevCwd = cwd
  cwd = resolved
  persistCwd()
}

async function handleCat(arg: string, ctx: CommandCtx): Promise<void> {
  if (!arg) {
    await streamDim(ctx, 'cat: no file specified.')
    return
  }

  const target = resolvePath(arg, cwd)
  const result = await fs.read(target)

  if (typeof result !== 'string') {
    if (result.error === 'binary') {
      await streamDim(ctx, `cat: ${basename(target)}: binary file. probably watch instead.`)
      await streamDim(ctx, '> hint: click the filename or use \'play <file>\'.')
      return
    }
    await streamDim(ctx, `cat: ${arg}: no such file.`)
    return
  }

  // Stream file content in dim at 28 cps
  const lines = result.split('\n')
  for (const line of lines) {
    const target2 = createStreamTarget(ctx.history, 'vt-line-shell')
    target2.style.color = 'var(--vt-green-dim)'
    await ctx.streamFn(target2, line, { signal: ctx.abortSignal })
  }
}

async function handlePwd(ctx: CommandCtx): Promise<void> {
  const display = cwd === '/' ? '~' : `~${cwd}`
  await streamDim(ctx, display)
}

async function handlePlay(arg: string, ctx: CommandCtx): Promise<void> {
  if (!arg) {
    await streamDim(ctx, 'play: no file specified.')
    return
  }

  const target = resolvePath(arg, cwd)
  const resolved = await resolveDisplayName(target)
  const stat = await fs.stat(resolved)

  if ('error' in stat) {
    await streamDim(ctx, `play: ${arg}: no such file.`)
    return
  }

  if (!fs.isVideo(stat)) {
    await streamDim(ctx, `play: ${arg}: not a video file.`)
    return
  }

  const fileNode = stat as fs.FsFileNode
  emit('filesystem:play', { path: fileNode.urlPath, displayName: fileNode.displayName })
}

// Resolve display-name paths to the virtual FS paths the FS module expects
// e.g., "S01 - THE KING'S MATHEMAGICKS" → find matching directory
async function resolveDisplayName(path: string): Promise<string> {
  // Check if the path is already resolvable
  const directStat = await fs.stat(path)
  if (!('error' in directStat)) return path

  // Try to resolve by matching display names under VPK_SERIES
  if (path.startsWith('/VPK_SERIES/')) {
    const rest = path.slice('/VPK_SERIES/'.length)
    // Split into segments
    const segments = rest.split('/').filter(Boolean)
    if (segments.length === 0) return path

    // Try to find matching entry
    // For each segment, look through index entries
    let currentPath = '/VPK_SERIES'
    for (const segment of segments) {
      const children = await fs.list(currentPath)
      if ('error' in children) return path

      let found = false
      for (const child of children) {
        // Match by display name (case-insensitive) or by exact name
        if (child.displayName.toLowerCase() === segment.toLowerCase() ||
            child.name.toLowerCase() === segment.toLowerCase()) {
          currentPath = currentPath === '/' ? `/${child.name}` : `${currentPath}/${child.name}`
          found = true
          break
        }
      }
      if (!found) return path
    }
    return currentPath
  }

  return path
}

function persistCwd(): void {
  set('shellCwd', cwd === '/' ? null : cwd)
}

export function restoreCwd(): void {
  const s = load()
  const saved = s.shellCwd
  if (saved) {
    cwd = saved
  }
}

async function streamDim(ctx: CommandCtx, text: string): Promise<void> {
  const target = createStreamTarget(ctx.history, 'vt-line-shell')
  target.style.color = 'var(--vt-green-dim)'
  await ctx.streamFn(target, text, { signal: ctx.abortSignal })
}

function scheduleAnchor(): void {
  const fn = (window as unknown as Record<string, unknown>).__vtScheduleAnchor
  if (typeof fn === 'function') (fn as () => void)()
}

// Override handleCd to use the ctx-aware version when available
export { handleCdWithCtx as handleCdCtx }
